import React, { useCallback, useEffect } from "react";

import { isAxiosError } from "axios";
import { FormProvider, useForm } from "react-hook-form";

import { GoalBasedInsuranceTypeSchema } from "@fartherfinance/frontend/api/FinancialPlans/insurance/types";

import {
  InsuranceCarrierField,
  PolicyNumberField,
} from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Insurance/constants";
import { PickInsuranceFormFields } from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Insurance/Types";
import {
  AmountField,
  NameField,
  OwnerField,
} from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/constants";
import FormDrawer from "@src/multiCustodian/components/FinancialPlans/CreateUpdatePlanFlow/pages/Shared/FormDrawer/FormDrawer";
import type { ErrorMessage } from "@src/multiCustodian/components/TaxBudgets/reducer/types";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import FlexWrapper from "@src/sharedComponents/Forms/FlexWrapper";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import FormNumberField from "@src/sharedComponents/Forms/FormNumberField";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import removeCommas from "@src/utils/CommaHelperFunctions/removeCommas";
import {
  formNumberFieldWithCommasCustomRegex,
  formNumberFieldWithCommasRules,
} from "@src/utils/formValidation";

import { InsuranceDrawerProps } from "./types";

type PickedFormFields = PickInsuranceFormFields<
  | "OwnerField"
  | "NameField"
  | "AmountField"
  | "InsuranceCarrierField"
  | "PolicyNumberField"
>;

type Form = PickedFormFields;

const LongTermCareInsuranceForm = ({
  goalBasedFinancialPlanId,
  selectedInsurance,
  defaultOwner,
  drawerOpen,
  ownerOptions,
  onClose,
  postGoalBasedFinancialPlanInsurance,
  patchGoalBasedFinancialPlanInsurance,
  isLoading,
  onRemove,
}: InsuranceDrawerProps) => {
  const statusNotification = useStatusNotification();

  const methods = useForm<Form>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      [OwnerField]: defaultOwner,
      [NameField]: "",
      [AmountField]: "",
      [InsuranceCarrierField]: undefined,
      [PolicyNumberField]: undefined,
    },
  });

  const {
    getValues,
    reset,
    formState: { isDirty, isValid },
  } = methods;

  const setFormFields = useCallback(() => {
    if (drawerOpen) {
      reset({
        [OwnerField]: defaultOwner,
        [NameField]: selectedInsurance?.name,
        [AmountField]: selectedInsurance?.amount,
        [InsuranceCarrierField]: selectedInsurance?.insuranceCarrier,
        [PolicyNumberField]: selectedInsurance?.policyNumber,
      });
    }
  }, [drawerOpen, selectedInsurance, reset, defaultOwner]);

  useEffect(() => {
    setFormFields();
  }, [setFormFields]);

  const handleOnClose = () => {
    setFormFields();
    onClose();
  };

  const handleOnSave = async () => {
    const values = getValues();

    if (values[OwnerField] === undefined) {
      return;
    }

    if (values[AmountField] === undefined) {
      return;
    }

    if (values[NameField] === undefined) {
      return;
    }

    const body = {
      insuranceType: GoalBasedInsuranceTypeSchema.Enum.LongTerm,
      owner: values[OwnerField].value,
      name: values[NameField],
      amount: removeCommas(values[AmountField]),
      insuranceCarrier: values[InsuranceCarrierField],
      policyNumber: values[PolicyNumberField],
      file: undefined, // API is not setup yet
    };

    try {
      if (selectedInsurance) {
        await patchGoalBasedFinancialPlanInsurance({
          goalBasedFinancialPlanId,
          goalBasedFinancialPlanInsuranceId: selectedInsurance.planInsuranceId,
          body,
        });

        statusNotification("Insurance added", "Success");
      } else {
        await postGoalBasedFinancialPlanInsurance({
          goalBasedFinancialPlanId,
          body,
        });

        statusNotification("Insurance added", "Success");
      }

      handleOnClose();
    } catch (error) {
      if (isAxiosError<ErrorMessage>(error)) {
        const message = error.response?.data.message;
        statusNotification(`Error adding insurance: ${message ?? ""}`, "Error");
      } else {
        statusNotification(
          "There was an error adding the insurance. Please try again.",
          "Error"
        );
      }
    }
  };

  return (
    <FormDrawer
      title="Long-Term Care Insurance"
      drawerOpen={drawerOpen}
      onClose={handleOnClose}
      saveDisabled={isLoading || !isValid || !isDirty}
      onSave={handleOnSave}
      removeDisabled={isLoading}
      onRemove={selectedInsurance ? onRemove : undefined}
      isLoading={isLoading}
      hasError={false}
      deleteModalTitle={"Remove Insurance"}
      deleteModalSubTitle={"Are you sure you want to remove this policy?"}
    >
      <FormProvider {...methods}>
        <FlexWrapper>
          <FormDropdownField
            name={OwnerField}
            label={"Owner"}
            values={ownerOptions}
            disabled={selectedInsurance !== undefined}
            disableSearch={true}
            required={"Must not be empty"}
          />

          <FormTextField
            name={NameField}
            label="Name"
            required={"Must not be empty"}
          />
        </FlexWrapper>

        <FlexWrapper>
          <FormNumberField
            name={AmountField}
            label="Coverage Amount"
            startAdornment={"$"}
            customRegex={formNumberFieldWithCommasCustomRegex}
            rules={formNumberFieldWithCommasRules({
              required: true,
              minValue: 0,
              maxValue: 1_000_000_000,
            })}
            autoAddCommas={true}
          />
        </FlexWrapper>

        <FlexWrapper>
          <FormTextField
            name={InsuranceCarrierField}
            label="Insurance Carrier"
            required={"Must not be empty"}
          />

          <FormTextField
            name={PolicyNumberField}
            label="Policy Number"
            required={"Must not be empty"}
          />
        </FlexWrapper>
      </FormProvider>
    </FormDrawer>
  );
};

export default LongTermCareInsuranceForm;
